import { RefObject, useEffect, useRef, useState } from "react";

export type CameraStreamState = "initializing" | "playing" | "refused";
export type CameraTorchState = "on" | "off" | "unsupported" | "unknown";

export const useCameraStream = (elementRef: RefObject<HTMLVideoElement>, cameraDeviceId: string, withTorch: boolean,
                                onError?: (message: string) => void) => {
    const [state, setState] = useState<CameraStreamState>("initializing");
    const [torchState, setTorchState] = useState<CameraTorchState>("unknown");

    const streamRef = useRef<MediaStream | null>();

    useEffect(() => {
        (async () => {
            try {
                if (!elementRef.current) {
                    return;
                }

                setState("initializing");

                if (streamRef.current) {
                    elementRef.current.pause();
                    streamRef.current.getTracks().forEach(track => track.stop());
                }

                streamRef.current = await navigator
                    .mediaDevices
                    .getUserMedia({
                        video: {
                            deviceId: {
                                ideal: cameraDeviceId,
                            },
                            facingMode: "environment",
                        },
                    });
                setTorchState("unknown");

                elementRef.current.srcObject = streamRef.current;
                if (!streamRef.current.getVideoTracks()[0].getCapabilities().torch) {
                    setTorchState("unsupported");
                }

                setState("playing");
            } catch (err) {
                if (onError) {
                    onError(`${err.name} ${err.message}`);
                }

                setState("refused");
            }
        })();

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }

            if (elementRef.current) {
                elementRef.current.pause();
            }
        };
    }, [elementRef, cameraDeviceId]);

    useEffect(() => {
        if (streamRef.current) {
            const videoTrack = streamRef.current.getVideoTracks()[0];
            if (!videoTrack) {
                return;
            }

            try {
                const capabilities = videoTrack.getCapabilities();
                if (capabilities.torch) {
                    videoTrack.applyConstraints({ advanced: [{ torch: withTorch }] });
                    setTorchState(withTorch ? "on" : "off");
                } else {
                    setTorchState("unsupported");
                }
            } catch (err) {
                setTorchState("unsupported");
            }
        }
    }, [withTorch]);

    return {
        streamState: state,
        torchState,
    };
};
